// Plain-data readings for the v2 board's cells (GRO-276 design loop). No JSX here, so each one
// is testable under `node --test` and the three layouts draw from one set of facts.
//
// Every reading returns null rather than an apology. The v1 board printed "Couldn't find out"
// in two columns on most rows; v2 leaves the cell blank and explains it in the header tooltip.

import type { AccountSignal, BookAccount, Opportunity, Viewer, WhyNowLine } from '../api/types';
import { WHY_NOW_RANK, lastOutbound } from './derived.ts';
import type { RankedAccount, WorkReason } from './derived.ts';
import { formatUsd, fundShortName, readFunding, readRelationship } from './funding.ts';
import { isInternalSignal } from './signalCopy.ts';
import { daysSince, titleCase } from '../lib/format.ts';
import { firstLinkIn, safeHref } from '../sidebar/overviewSource.ts';
import { withoutQ4Prefix } from '../lib/q4Target.ts';

// The v2 board (GRO-276), on unless the viewer block says `board_v2: false` (`BOARD_V2=off` on
// the server). Missing means on: v2's own server always sends the field.
export function boardIsV2(viewer: Viewer | undefined): boolean {
  return viewer?.board_v2 !== false;
}

// Pill tints, the Q4 triage page's four plus a neutral.
export type PillTone = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

// --- Small formatters ------------------------------------------------------------------------

export function compactCount(value: number | null | undefined): string {
  if (value == null) return '';
  if (value >= 1e6) return (value / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (value >= 1e4) return Math.round(value / 1e3) + 'k';
  if (value >= 1e3) return (value / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(Math.round(value));
}

// "14 Jul": the year is noise in a narrow cell, and the age beside it says how long ago.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function dayMonth(day: string): string {
  const month = Number(day.slice(5, 7));
  const date = Number(day.slice(8, 10));
  if (!month || !date) return day;
  return `${date} ${MONTHS[month - 1]}`;
}

// "2026-07" → "Jul 26".
export function monthShort(month: string | null | undefined): string {
  if (!month) return '';
  const index = Number(month.slice(5, 7));
  if (!index) return month;
  return `${MONTHS[index - 1]} ${month.slice(2, 4)}`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '';
  return name.split(/\s+/).filter(Boolean).map(part => part[0]!.toUpperCase()).slice(0, 2).join('');
}

function bookToday(row: RankedAccount): Date | null {
  return row.book_today ? new Date(row.book_today + 'T00:00:00Z') : null;
}

// --- Employees: the sub-note under (or beside) the account name ------------------------------

export function employeesLabel(row: BookAccount): string | null {
  const employees = row.employee_count ?? row.clay?.engine?.employees ?? null;
  return employees == null ? null : compactCount(employees) + ' emp';
}

// --- Why now: a pill and the signal's age ----------------------------------------------------

// Ranking reasons that say nothing a row doesn't already show. Repeated on every untouched row.
const BOILERPLATE_DETAIL = [
  /^no outbound activity recorded/i,
  /^no touch in \d+ days$/i,
];

export interface WhyNowReading {
  label: string;
  tone: PillTone;
  // "2d" since the signal was seen, when we know when that was.
  age: string | null;
  // The ranking's reason, minus boilerplate. Null when there is nothing to add.
  detail: string | null;
  // Where it came from and when, for the tooltip only.
  provenance: string;
}

function ageLabel(stamp: string | null, today: Date | null): string | null {
  if (!stamp || !today) return null;
  const days = daysSince(stamp.slice(0, 10), today);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return 'today';
  if (days < 60) return days + 'd';
  return Math.round(days / 30) + 'mo';
}

function toneOfReason(reason: WorkReason): PillTone {
  if (reason.kind === 'signal') return 'good';
  if (reason.kind === 'usage_climbing') return 'info';
  if (reason.tone === 'warn') return 'warn';
  return 'neutral';
}

export function whyNowReading(row: RankedAccount): WhyNowReading | null {
  const reason = row.work_reason ?? null;
  if (!reason) return null;
  const stamp = reason.observed_at ?? row.clay?.signal?.last_seen ?? null;
  const detail = reason.detail && !BOILERPLATE_DETAIL.some(pattern => pattern.test(reason.detail!.trim()))
    ? reason.detail
    : null;
  const source = reason.source === 'scout' ? 'Scout ranking' : 'Book snapshot';
  const computed = reason.computed_at ? ' · ' + reason.computed_at.slice(0, 10) : '';
  return {
    label: reason.label,
    tone: toneOfReason(reason),
    age: reason.kind === 'signal' ? ageLabel(stamp, bookToday(row)) : null,
    detail,
    provenance: source + computed,
  };
}

// --- Last touch -------------------------------------------------------------------------------

export interface LastTouchReading {
  day: string;
  age: string | null;
  days: number | null;
  who: string | null;
  // Initials of the rep, when it was not the owner: the collision flag.
  other_rep: string | null;
  other_rep_name: string | null;
}

export function lastTouchReading(row: BookAccount, today: Date): LastTouchReading | null {
  const last = lastOutbound(row);
  if (!last) return null;
  const days = daysSince(last.day, today);
  return {
    day: dayMonth(last.day),
    age: days >= 0 ? days + 'd' : null,
    days: days >= 0 ? days : null,
    who: last.who,
    other_rep: last.byOther ? initials(last.author) : null,
    other_rep_name: last.byOther ? last.author : null,
  };
}

// --- Opp --------------------------------------------------------------------------------------

function monthYear(day: string | null): string {
  if (!day) return '';
  return day.slice(5, 7) + '/' + day.slice(2, 4);
}

// "2 - Discovery" → "S2 Discovery"; anything else passes through.
function shortStage(stage: string | null): string {
  if (!stage) return 'Open';
  const numbered = /^(\d)\s*[-–.:]\s*(.+)$/.exec(stage.trim());
  return numbered ? `S${numbered[1]} ${numbered[2]}` : stage;
}

function dealDay(deal: Opportunity): string {
  return deal.close_date || deal.created || '';
}

// --- Plan and usage ---------------------------------------------------------------------------

export interface UsageReading {
  credits: string;
  trend_pct: number | null;
  // Managed Agents minutes in the last 30 days, compact ("65K"), or null with none (GRO-450).
  agent_minutes: string | null;
  // The cell's text: the credits, or "0 cr · 65K min" when the account also bills agents.
  text: string;
}

// The cell shows credits only. Agent minutes stay on the reading for the hover's agents rows
// (agentUsage.ts); an agents-only account reads 0 credits.
export function usageReading(row: BookAccount): UsageReading | null {
  const minutes = row.agent_minutes_30 ?? 0;
  if (row.credits_30 == null && minutes <= 0) return null;
  const credits = compactCount(row.credits_30 ?? 0);
  const agentMinutes = minutes > 0 ? (minutes < 1 ? '<1' : compactCount(minutes)) : null;
  return {
    credits,
    trend_pct: row.credits_trend_pct == null ? null : Math.round(row.credits_trend_pct),
    agent_minutes: agentMinutes,
    text: credits,
  };
}

// --- a short why-now phrase --------------------------------------------------------

const SHORT_REASON_WORDS = 5;

// A real phrase of at most five words, built from the reason's kind and its key fact, never a
// truncated sentence: "Raised $40M Series B", "Hiring Staff Voice AI", "3 pricing visits",
// "Paid usage up 64%". The full sentence goes in the popover.
const NUMBER_WORDS: Record<string, string> = {
  one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
};

function capitalise(text: string): string {
  return text ? text[0]!.toUpperCase() + text.slice(1) : text;
}

function limitWords(text: string): string {
  return text.trim().split(/\s+/).slice(0, SHORT_REASON_WORDS).join(' ');
}

function signalPhrase(label: string, detail: string): string {
  const both = `${label} ${detail}`;
  const lower = both.toLowerCase();

  // Funding: "Raised $40M Series B".
  if (/raised|series [a-e]\b|funding|round/.test(lower)) {
    const amount = /\$\d+(?:\.\d+)?\s?[KMB]/i.exec(both)?.[0]?.replace(/\s/g, '') ?? '';
    const round = /series [a-e]|seed|growth/i.exec(both)?.[0] ?? '';
    return limitWords(['Raised', amount, round ? capitalise(round) : ''].filter(Boolean).join(' '));
  }

  // Hiring: the job from "Posted a Staff Voice AI Engineer role", else only that they hire.
  if (/hiring|job posting|posted .*role/.test(lower)) {
    const title = postingTitle(detail) ?? postingTitle(label);
    return title ? limitWords('Hiring: ' + title) : 'Hiring: voice/support role';
  }

  // Web visits: "3 pricing visits".
  if (/pricing|visit/.test(lower)) {
    const count = /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\b visits?/i.exec(detail)?.[1];
    const number = count ? (NUMBER_WORDS[count.toLowerCase()] ?? count) : '';
    return limitWords([number, 'pricing', number === '1' ? 'visit' : 'visits'].filter(Boolean).join(' '));
  }

  // A senior hire: "New VP Eng".
  if (/^new\b/i.test(label)) return limitWords(label.replace(/\b(vp|cto|ceo|coo|cpo)\b/gi, word => word.toUpperCase()).replace(/^new/i, 'New'));
  return limitWords(capitalise(label));
}

// rank_books' last rung, written when nothing else applied. It says nothing about the account.
const RANKER_DEFAULT_REASON = /^on the book\b/i;

// Words that open a sentence without being anybody's name ("A colleague left a note.").
const NOT_A_FIRST_NAME = new Set(['a', 'an', 'the', 'on', 'no', 'team', 'note', 'our', 'their']);

// "Kabir met their CTO at the summit." → "Kabir"; null when the sentence names nobody.
function noteAuthor(detail: string): string | null {
  const first = /^([A-Z][a-z]+)\s+[a-z]/.exec(detail.trim())?.[1] ?? null;
  if (!first || NOT_A_FIRST_NAME.has(first.toLowerCase())) return null;
  return first;
}

// The ranking reason's phrase: the fallback when the row carries no best_signal.
function reasonPhrase(row: RankedAccount): string | null {
  const reason = row.work_reason ?? null;
  if (!reason) return null;
  const detail = reason.detail ?? '';
  switch (reason.kind) {
    case 'signal':
      return signalPhrase(reason.label, detail);
    case 'usage_climbing': {
      const percent = row.credits_trend_pct != null ? Math.round(row.credits_trend_pct) : Number(/(\d+)%/.exec(reason.label)?.[1]);
      const paid = row.tier && row.tier !== 'free';
      return Number.isFinite(percent) && percent ? `${paid ? 'Paid usage' : 'Usage'} up ${percent}%` : 'Usage climbing';
    }
    case 'usage_stopped':
      return 'Usage dropped to zero';
    case 'overage': {
      const percent = /(\d+)%/.exec(detail)?.[1];
      return percent ? `${percent}% over plan` : 'Over its plan';
    }
    case 'tier':
      return limitWords(reason.label + ' fit');
    case 'note': {
      // The ranker's default ("On the book; no stronger reason today.") is not a note: say
      // nothing rather than "Note from On".
      if (RANKER_DEFAULT_REASON.test(detail) || /^on the book$/i.test(reason.label)) return null;
      const author = noteAuthor(detail);
      return author ? `Note from ${author}` : 'Team note';
    }
    case 'quiet': {
      const days = /(\d+) days?/.exec(detail)?.[1];
      if (days) return `Quiet for ${days} days`;
      return /not contacted/i.test(reason.label) ? 'Not contacted yet' : capitalise(limitWords(reason.label));
    }
  }
  return limitWords(capitalise(reason.label));
}

// --- Why now from best_signal (W3) ------------------------------------------------------------

// A round older than this is history, not a reason to call today. It stays in the Funding
// column and the hover card; best_signal.py applies the same bar when it builds the list.
export const FUNDING_WHY_NOW_DAYS = 180;

function isWhyNowWorthy(signal: AccountSignal, today: Date | null): boolean {
  if (signal.kind !== 'funding' || !signal.on || !today) return true;
  return daysSince(signal.on.slice(0, 10), today) <= FUNDING_WHY_NOW_DAYS;
}

// An event at the company itself: a launch, a round, a hire, a new leader (jobs/why_now_pass.py
// COMPANY_EVENT_KINDS). A fresh one outranks anything about product use, however fresh that is.
export const COMPANY_EVENT_KINDS: ReadonlySet<string> = new Set(['product_launch', 'funding', 'job_posting', 'job_changer',
  'other_voice_provider', 'voice_product', 'voice_platform']);
export const PRODUCT_USE_KINDS: ReadonlySet<string> = new Set([
  'playground_user', 'paid_admin', 'usage_climbing', 'usage_stopped', 'usage', 'playground_org', 'plan',
]);

// A launch, round, hire or leader dated within the strong window of the book's day. A month-only
// date counts from the month's first day: an unknown day never outranks a dated login.
export function isFreshCompanyEvent(signal: AccountSignal, today: Date | null): boolean {
  if (!COMPANY_EVENT_KINDS.has(signal.kind) || !signal.on || !today) return false;
  const day = isMonthOnly(signal.on) ? `${signal.on}-01` : signal.on.slice(0, 10);
  const age = daysSince(day, today);
  return Number.isFinite(age) && age >= 0 && age <= PASS_STRONG_WINDOW_DAYS;
}

function signalCandidates(row: RankedAccount): AccountSignal[] {
  // An empty list is no ranking: fall back to the two picked signals, as for a missing one.
  return row.signals?.length
    ? row.signals
    : [row.best_quotable_signal, row.best_timing_signal].filter((entry): entry is AccountSignal => Boolean(entry));
}

// The row's first fresh company event, in best_signal's order; null when it has none.
export function freshCompanyEvent(row: RankedAccount): AccountSignal | null {
  const today = bookToday(row);
  return signalCandidates(row).find(entry => isFreshCompanyEvent(entry, today)) ?? null;
}

// The row's strongest signal: best_signal.py ranks `signals` best first, but a fresh company
// event outranks product use (a playground user, a paid admin, usage) ranked above it.
export function bestSignal(row: RankedAccount): AccountSignal | null {
  const today = bookToday(row);
  const best = signalCandidates(row).find(entry => isWhyNowWorthy(entry, today)) ?? null;
  if (best && PRODUCT_USE_KINDS.has(best.kind)) return freshCompanyEvent(row) ?? best;
  return best;
}

// Senior titles the sweep's one-line reason may name, longest match first.
const LEADERSHIP_TITLES = /\b(chief [a-z]+ officer|c[eotfpmr]o|(?:s?vp|svp|evp|vice president)(?: of)? [a-z&/]+(?: [a-z&/]+)?|head of [a-z&/]+(?: [a-z&/]+)?|(?:co-)?founder|president|general manager|(?:senior |sr\.? )?director(?: of [a-z]+)?)\b/i;

const CHIEF_SHORT: Record<string, string> = {
  'chief executive officer': 'CEO', 'chief technology officer': 'CTO', 'chief operating officer': 'COO',
  'chief product officer': 'CPO', 'chief financial officer': 'CFO', 'chief marketing officer': 'CMO',
  'chief revenue officer': 'CRO', 'chief information officer': 'CIO', 'chief scientific officer': 'CSO',
};

const DEPARTMENT_SHORT: Record<string, string> = {
  engineering: 'Eng', product: 'Product', operations: 'Ops', marketing: 'Marketing', sales: 'Sales',
  technology: 'Tech', research: 'Research', customer: 'Customer', growth: 'Growth', finance: 'Finance',
};

// "Acme appointed Jane Doe as Chief Technology Officer" → "CTO"; "hired a VP of Engineering" →
// "VP Eng". Null when the line names no role.
export function leadershipRole(text: string): string | null {
  const match = LEADERSHIP_TITLES.exec(text)?.[1];
  if (!match) return null;
  const lower = match.toLowerCase().replace(/\s+/g, ' ');
  if (CHIEF_SHORT[lower]) return CHIEF_SHORT[lower]!;
  if (/^c[eotfpmr]o$/.test(lower)) return lower.toUpperCase();
  const words = lower.replace(/^(s?vp|svp|evp|vice president)( of)?/, 'VP').replace(/^head of/, 'Head of').split(' ');

  // "VP of Engineering from Google": the title ends where the sentence moves on.
  const kept: string[] = [];
  for (const word of words) {
    if (kept.length && TITLE_ENDS_BEFORE.has(word)) break;
    kept.push(word === 'of' ? word : DEPARTMENT_SHORT[word] ?? capitalise(word));
  }
  return kept.slice(0, 4).join(' ');
}

const TITLE_ENDS_BEFORE = new Set(['from', 'at', 'to', 'in', 'as', 'on', 'for', 'and', 'who', 'after', 'with', 'joins', 'joined']);

// Phrases that are only the signal's type restated, so they are not a job title.
const POSTING_TYPE_ONLY = /^(voice or support hiring|job posting|hiring for a voice role|hiring)$/i;
const POSTING_TITLE_WORDS = 4;

// What is left when the text names no role: a verb, an article or the word "role" itself.
const POSTING_NOT_A_TITLE = /^(?:an?|the|role|roles|position|positions|job|jobs|opening|openings|vacancy|seeking|hiring|posted|recruiting|looking|listed|for)$/i;

// "Posted a Staff Voice AI Engineer role" → "Staff Voice AI Engineer"; "Hiring a Head of
// Support in Austin" → "Head of Support". Null when the text is missing, only the type
// restated, or no title at all ("Hiring role", "Seeking", "Hiring a"), so the cell falls back.
export function postingTitle(text: string | null | undefined): string | null {
  if (typeof text !== 'string') return null;
  const cleaned = text.trim().replace(/[.!]$/, '');
  if (!cleaned || POSTING_TYPE_ONLY.test(cleaned)) return null;
  const verb = /(?:posted|hiring|seeking|recruiting|looking for|opening for|listed)(?: for)?(?: an?| the)? (.+?)(?: (?:role|position|job|opening|vacancy)s?\b|[,.;(]| (?:at|in|on|to|for) \b|$)/i.exec(cleaned);
  const title = (verb?.[1] ?? cleaned).replace(/^(an?|the) /i, '').trim();
  if (!title || POSTING_TYPE_ONLY.test(title) || POSTING_NOT_A_TITLE.test(title)) return null;
  return title.split(/\s+/).slice(0, POSTING_TITLE_WORDS).join(' ');
}

// What a launch sentence says was launched, up to the first clause break.
const LAUNCHED_PRODUCT = /\b(?:launched|launches|unveiled|unveils|introduced|introduces|released|releases)\s+(?:its |a |an |the |new )*(.+?)(?:,|;|\.\s| — | that | which | to | for | with |$)/i;

// best_signal appends " · 25 Aug" to every dated fact; the cell shows the age instead.
function factWithoutDay(fact: string): string {
  return fact.replace(/\s·\s[^·]*$/, '').trim();
}

function personFirstName(signal: AccountSignal, fact: string): string {
  return firstName(signal.person_name) ?? firstName(fact.split(' ')[0]) ?? 'Someone';
}

const SIGNAL_TONES: Record<string, PillTone> = {
  other_voice_provider: 'good', voice_product: 'good', voice_platform: 'good', funding: 'good', job_posting: 'good', job_changer: 'good', product_launch: 'good', web_intent: 'good', replied: 'good',
  usage_climbing: 'info', paid_admin: 'info', playground_user: 'info',
  usage_stopped: 'warn',
  // The Q4 research's why-now: reviewed news is a reason; Exa news nobody checked is only a lead.
  q4_research: 'good', q4_research_unreviewed: 'neutral',
};

// Clay's evidence leads with its type ("voice product - Builds a voice product. ..."), and best_signal
// keeps that lead in the fact; the pill quotes only the words after it.
const CLAY_TYPE_LEAD = /^(?:other voice provider|voice product|voice platform)\s+-\s+/i;

// A real phrase of at most five words for one best_signal fact.
export function signalPhraseOf(signal: AccountSignal, row: BookAccount): string {
  // A Q4 why-now's fact opens "Q4 research: "; a round or a hire under it reads like any other.
  const fact = withoutQ4Prefix(factWithoutDay(signal.fact));
  const after = (fact.includes(':') ? fact.slice(fact.indexOf(':') + 1).trim() : fact).replace(CLAY_TYPE_LEAD, '');
  switch (signal.kind) {
    case 'usage_climbing': {
      const percent = /up (\d+)%/.exec(fact)?.[1];
      const paid = row.tier && row.tier !== 'free';
      return percent ? `${paid ? 'Paid usage' : 'Usage'} up ${percent}%` : 'Usage climbing';
    }
    case 'usage_stopped':
      return 'Usage stopped';
    case 'other_voice_provider': {
      // Clay's "Uses ElevenLabs. <summary>" → "Uses ElevenLabs".
      const first = after.split('.')[0]?.trim() ?? '';
      return first ? limitWords(first) : 'Uses another voice provider';
    }
    case 'voice_product': {
      // Clay's "Builds a voice product. <summary>" → "Builds a voice product".
      const first = after.split('.')[0]?.trim() ?? '';
      return first ? limitWords(first) : 'Builds a voice product';
    }
    case 'voice_platform': {
      // Clay's "Runs voice on LiveKit. <summary>" → "Runs voice on LiveKit".
      const first = after.split('.')[0]?.trim() ?? '';
      return first ? limitWords(first) : 'Runs voice on a platform';
    }
    case 'paid_admin':
      return `${personFirstName(signal, fact)} is a paid admin`;
    case 'playground_user':
      return `${personFirstName(signal, fact)} uses the playground`;
    case 'replied':
      return `${personFirstName(signal, fact)} replied`;
    case 'job_changer': {
      // A hero carries the person; the sweep's "Leadership change: <reason>" carries a role.
      if (signal.person_name) return `${firstName(signal.person_name)} joined recently`;
      const role = leadershipRole(after);
      return role ? `New ${role}` : 'Leadership change';
    }
    case 'product_launch': {
      // The sweep's "Liberate launched AI Intercept, a ..." → "Launched AI Intercept".
      const product = LAUNCHED_PRODUCT.exec(after)?.[1]?.trim();
      return product ? limitWords('Launched ' + product) : 'Voice product launch';
    }
    case 'job_posting': {
      const title = postingTitle(after);
      return title ? `Hiring: ${title}` : 'Hiring: voice/support role';
    }
    case 'clay_signal':
      // An older payload's "Clay signal: lookalike"; the name is the fact.
      return /lookalike/i.test(after) ? 'Lookalike' : limitWords(capitalise(after));
    case 'lookalike':
      return /^lookalike of /i.test(fact) ? limitWords(fact) : 'Lookalike';
    case 'funding': {
      const amount = /\$([\d,]+)/.exec(fact)?.[1];
      const round = /^Raised (.+?)(?: of \$|$)/.exec(fact)?.[1] ?? '';
      const money = amount ? formatUsd(Number(amount.replace(/,/g, ''))) : '';
      return limitWords(['Raised', money, round].filter(Boolean).join(' '));
    }
    case 'web_intent': {
      const visits = /(\d+) website visits?/.exec(fact)?.[1];
      return visits ? `${visits} website visits` : 'Visited the website';
    }
    case 'shared_investor': {
      const fund = after.split(',')[0]?.trim() ?? '';
      return fund ? limitWords('Backed by ' + fundShortName(fund)) : 'Shared investor';
    }
    case 'q4_research':
    case 'q4_research_unreviewed':
      return limitWords(capitalise(fact));
    case 'paid_tier': {
      const tier = /paid (\w+) plan/.exec(fact)?.[1];
      return tier ? `On the ${titleCase(tier)} plan` : 'On a paid plan';
    }
  }
  return limitWords(capitalise(fact.split(':')[0]!));
}

export interface WhyNowCellReading {
  phrase: string;
  tone: PillTone;
  age: string | null;
  // The best_signal behind the phrase; null when it fell back to the ranking reason.
  signal: AccountSignal | null;
  // The weekly pass's line behind the phrase (P4-04); null when the cell is computed here.
  pass: WhyNowLine | null;
}

// Green for a fresh, dated reason; orange for an evergreen or older one.
const PASS_TONES: Record<WhyNowLine['strength'], PillTone> = { strong: 'good', weak: 'warn' };

// The job's strong window (jobs/why_now_pass.py STRONG_WINDOW_DAYS). A line stays up to a week.
export const PASS_STRONG_WINDOW_DAYS = 30;

function isMonthOnly(sourceOn: string | null): boolean {
  return sourceOn != null && /^\d{4}-\d{2}$/.test(sourceOn);
}

// The last day a source date covers: the day itself, or the month's last day for "YYYY-MM".
function sourceEndDay(sourceOn: string): string {
  if (!isMonthOnly(sourceOn)) return sourceOn.slice(0, 10);
  const [year, month] = sourceOn.split('-').map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${sourceOn}-${String(lastDay).padStart(2, '0')}`;
}

// A person's standing is green exactly when their own event, the last use, is in the window
// (jobs/why_now_pass.py EVENT_DATED_KINDS): the date decides, not the strength the line was written with.
export const EVENT_DATED_PASS_KINDS: ReadonlySet<string> = new Set(['paid_admin', 'playground_user']);

// Strong as written, unless its source has aged past the window since the pass wrote it.
export function passStrength(row: RankedAccount): WhyNowLine['strength'] | null {
  const pass = row.why_now;
  if (!pass) return null;
  const eventDated = pass.source_kind != null && EVENT_DATED_PASS_KINDS.has(pass.source_kind);
  if (pass.strength !== 'strong' && !eventDated) return 'weak';
  const today = bookToday(row);
  if (!pass.source_on || !today) return pass.source_on && !eventDated ? 'strong' : 'weak';
  const age = daysSince(sourceEndDay(pass.source_on), today);
  return Number.isFinite(age) && age <= PASS_STRONG_WINDOW_DAYS ? 'strong' : 'weak';
}

// The pass runs weekly per account, so a knock that landed after it wrote is news it has not
// read: the signal shows until the next pass. Web visits re-stamp daily, so they never count, and
// neither does a colleague's ask (chat, csv), which is not news at all (GRO-307).
export function passIsCurrent(row: RankedAccount): boolean {
  const pass = row.why_now;
  if (!pass) return false;
  const slot = row.clay?.signal;
  if (!slot?.name || !slot.last_seen || /web intent/i.test(slot.name) || isInternalSignal(slot.name)) return true;
  return slot.last_seen.slice(0, 10) <= pass.written_at.slice(0, 10);
}

// A pass line about product use is outranked by a fresh company event on the row: the pass
// predates the event's weighing, or was written before the rule (jobs/why_now_pass.py).
export function passIsOutranked(row: RankedAccount): boolean {
  const pass = row.why_now;
  if (!pass?.source_kind || !PRODUCT_USE_KINDS.has(pass.source_kind)) return false;
  return freshCompanyEvent(row) != null;
}

const VOICE_KINDS: ReadonlySet<string> = new Set(['other_voice_provider', 'voice_product', 'voice_platform']);

// When the best signal is a voice one, the pill lists every distinct voice signal on the row, best first:
// "Uses ElevenLabs · Runs voice on Vapi · Builds a voice product". The one slot decides routing, not what is shown.
export function voiceSignalsPhrase(best: AccountSignal, row: RankedAccount): string | null {
  if (!VOICE_KINDS.has(best.kind)) return null;
  const phrases: string[] = [];
  const seen = new Set<string>();
  for (const entry of [best, ...(row.signals ?? [])]) {
    if (!VOICE_KINDS.has(entry.kind) || seen.has(entry.kind)) continue;
    seen.add(entry.kind);
    phrases.push(signalPhraseOf(entry, row));
  }
  return phrases.join(' · ');
}

export function whyNowCell(row: RankedAccount): WhyNowCellReading | null {
  // The weekly pass's line wins when there is one, nothing newer has landed since, and no fresh
  // company event outranks the product use it cites.
  const pass = row.why_now ?? null;
  if (pass && passIsCurrent(row) && !passIsOutranked(row)) {
    return {
      phrase: pass.line,
      tone: PASS_TONES[passStrength(row) ?? 'weak'],
      // A month-only source ("2026-09") has no day to count from, so it gets no age pill.
      age: isMonthOnly(pass.source_on) ? null : ageLabel(pass.source_on, bookToday(row)),
      signal: null,
      pass,
    };
  }

  const signal = bestSignal(row);
  if (signal) {
    return {
      phrase: voiceSignalsPhrase(signal, row) ?? signalPhraseOf(signal, row),
      tone: SIGNAL_TONES[signal.kind] ?? 'neutral',
      age: ageLabel(signal.on, bookToday(row)),
      signal,
      pass: null,
    };
  }
  const reading = whyNowReading(row);
  const phrase = reasonPhrase(row);
  if (!reading || !phrase) return null;
  return { phrase, tone: reading.tone, age: reading.age, signal: null, pass: null };
}

// The pass stores each fact cut at this many characters (jobs/why_now_pass.py MAX_FACT_CHARACTERS).
export const PASS_FACT_CHARACTERS = 300;

// A cut fact can end inside a link ("… https://group.softbank/en/news/pr"): that half link would
// open a broken page, so it is dropped and the stored `source_url` carries the source instead.
// The " · 25 Aug" the pass appends after the cut, so the half link is found before it.
const FACT_DAY_SUFFIX = /\s·\s[^·]*$/;

export function withoutClippedLink(fact: string, clippedAt: number = PASS_FACT_CHARACTERS): string {
  const suffix = FACT_DAY_SUFFIX.exec(fact)?.[0] ?? '';
  const body = suffix ? fact.slice(0, fact.length - suffix.length) : fact;
  if (body.length < clippedAt) return fact;
  const cut = body.replace(/\s*https?:\/\/\S*$/, '').trimEnd();
  return cut + suffix;
}

// One signal's hover row text: the fact with its day, and its link when the words carry none.
export function signalRowText(signal: AccountSignal): string {
  const href = safeHref(signal.url);
  if (!href || firstLinkIn(signal.fact)) return signal.fact;
  return `${signal.fact} ${href}`;
}

// The pass card's rows: the detail, the fact it rests on, its link, how fresh, and when it was
// checked. The card renders every http(s) URL in a row as a link.
export function whyNowPassRows(row: RankedAccount): Array<[string, string]> {
  const pass = row.why_now;
  if (!pass) return [];
  const source = withoutClippedLink(pass.source_fact);
  const sourceUrl = safeHref(pass.source_url);
  const rows: Array<[string, string]> = [['Why', pass.detail], ['Source', source]];
  if (sourceUrl && !source.includes(sourceUrl)) rows.push(['Link', sourceUrl]);
  rows.push(['Strength', passStrength(row) === 'strong' ? 'Fresh' : 'Older or evergreen']);
  rows.push(['Checked', dayMonth(pass.written_at.slice(0, 10))]);
  return rows;
}

export function whyNowPhrase(row: RankedAccount): string | null {
  return whyNowCell(row)?.phrase ?? null;
}

// The hover card's Scout rows: "#3 · Paid pro plan · 30 Sep", then the reason only when it says
// more than the label did. The ranker's default rung adds nothing, so it adds no row.
export function rankingRows(row: RankedAccount): Array<[string, string]> {
  const reason = row.work_reason ?? null;
  if (!reason) return [];
  const detail = (reason.detail ?? '').trim();
  if (RANKER_DEFAULT_REASON.test(detail) || /^on the book$/i.test(reason.label)) return [];
  const rank = row.ranking != null ? `#${row.ranking} · ` : '';
  const computed = reason.computed_at ? ' · ' + dayMonth(reason.computed_at.slice(0, 10)) : '';
  const rows: Array<[string, string]> = [['Scout ranking', `${rank}${reason.label}${computed}`]];

  // "Paid pro plan" under the label "pro" is the same fact twice.
  const sameFact = detail.replace(/[.\s]+$/, '').toLowerCase() === reason.label.trim().toLowerCase()
    || detail.toLowerCase().includes(reason.label.trim().toLowerCase()) && detail.split(/\s+/).length <= 4;
  if (detail && !sameFact) rows.push(['Reason', detail]);
  return rows;
}

export function firstName(name: string | null | undefined): string | null {
  if (!name) return null;
  return name.trim().split(/\s+/)[0] || null;
}

// --- Vertical ----------------------------------------------------------------------

export interface VerticalReading {
  label: string;
  title: string;
}

// The OB Engine's enriched sector (`clay.engine.sector`, from the Clay workflow), else
// Salesforce's `industry` (triage/Salesforce), else the engine's own industry. There is no
// Apollo industry on the book payload; adding one would be a backend change.
// Long sector and industry names, shortened so the pill never truncates. Anything not listed
// keeps its first two words.
const SHORT_VERTICALS: Record<string, string> = {
  'contact centre': 'Contact centre', 'contact center': 'Contact centre',
  'conversational ai': 'Conv. AI', 'voice agents': 'Voice agents',
  'software development': 'Software', 'developer tools': 'Dev tools',
  'healthcare software': 'Health tech', 'clinical software': 'Health tech', 'healthcare': 'Healthcare',
  'industrial robotics': 'Robotics', 'robotics': 'Robotics',
  'streaming media': 'Media', 'media & gaming': 'Media', 'media and gaming': 'Media',
  'insurance tech': 'Insurtech', 'financial services': 'Finance', 'call analytics': 'Call analytics',
  'information technology & services': 'IT services', 'business process outsourcing': 'BPO',
};

export function shortVertical(name: string): string {
  const known = SHORT_VERTICALS[name.trim().toLowerCase()];
  if (known) return known;
  return name.trim().split(/\s+/).slice(0, 2).join(' ');
}

export function verticalReading(row: BookAccount): VerticalReading | null {
  const engine = row.clay?.engine;
  const name = engine?.sector || row.industry || engine?.industry || null;
  if (!name) return null;
  return { label: shortVertical(name), title: name };
}

// --- Relationship (opportunities and plan as one pill) -----------------------------

export interface RelationshipPill {
  label: string;
  tone: PillTone;
  // Which rung of the precedence ladder this account landed on, for tests and sorting.
  kind: 'competitor' | 'customer' | 'open_opp' | 'paid_self_serve' | 'self_serve' | 'closed_lost' | 'touched' | 'net_new';
  title: string;
}

// Higher wins the sort, matching the precedence below.
export const RELATIONSHIP_RANK: Record<RelationshipPill['kind'], number> = {
  competitor: 8, customer: 7, open_opp: 6, paid_self_serve: 5, self_serve: 4, closed_lost: 3, touched: 2, net_new: 1,
};


// Every fact the pill summarises, one per line, for the tooltip.
function relationshipFacts(row: BookAccount): string {
  const lines: string[] = [];
  for (const deal of row.opportunities) {
    const state = deal.won ? 'Closed Won' : deal.closed ? 'Closed Lost' : 'Open';
    lines.push(`${state}: ${deal.stage || 'no stage'}${deal.close_date ? ' · ' + deal.close_date : ''}${deal.name ? ' · ' + deal.name : ''}`);
  }
  if (row.tier) lines.push(`Plan: ${titleCase(row.tier)}`);
  if (row.org_count) lines.push(`Playground orgs: ${row.org_count}`);
  if (row.credits_30 != null) lines.push(`Credits 30d: ${Math.round(row.credits_30).toLocaleString()}`);
  if (row.status) lines.push(`Salesforce status: ${row.status}`);
  lines.push(`Owner touches: ${row.touches}`);
  return lines.join('\n');
}

// One pill per account. Precedence: Competitor > Customer / enterprise > Open opp > Paid
// self-serve · <plan> > Self-serve user > Closed Lost > Touched, no opp > Net new.
export function relationshipPill(row: BookAccount): RelationshipPill {
  const title = relationshipFacts(row);

  // Shown, never hidden: the owner sees why nothing proposes or pushes it (GRO-314).
  if (row.competitor) {
    return { label: 'Competitor', tone: 'bad', kind: 'competitor', title: 'Marked as a competitor in Salesforce.\n' + title };
  }
  const won = row.opportunities.some(deal => deal.won);
  if (won || row.status === 'Customer' || row.tier === 'enterprise') {
    const label = row.tier === 'enterprise' ? 'Customer · Enterprise' : 'Customer';
    return { label, tone: 'bad', kind: 'customer', title };
  }

  const open = row.opportunities.filter(deal => !deal.closed);
  if (open.length) {
    const deal = [...open].sort((left, right) => dealDay(right).localeCompare(dealDay(left)))[0]!;
    return { label: 'Open: ' + shortStage(deal.stage), tone: 'info', kind: 'open_opp', title };
  }

  // Any tier but free (enterprise is a customer above): the payload carries free, pro,
  // startup, scale and enterprise (connectors/snowflake.py TIER_LABELS).
  if (row.tier && row.tier !== 'free') {
    return { label: 'Paid · ' + titleCase(row.tier), tone: 'warn', kind: 'paid_self_serve', title };
  }
  if (row.tier === 'free' || row.org_count > 0) {
    return { label: 'Self-serve · Free', tone: 'neutral', kind: 'self_serve', title };
  }

  const lost = row.opportunities.filter(deal => deal.closed && !deal.won);
  if (lost.length) {
    const latest = [...lost].sort((left, right) => dealDay(right).localeCompare(dealDay(left)))[0]!;
    return { label: 'Closed Lost ' + monthYear(dealDay(latest)), tone: 'bad', kind: 'closed_lost', title };
  }

  if (row.touches > 0 || lastOutbound(row)) {
    return { label: 'Touched, no opp', tone: 'neutral', kind: 'touched', title };
  }
  return { label: 'Net new', tone: 'good', kind: 'net_new', title };
}

// --- Funding and Our VC, short and never truncated ---------------------------------

export interface FundingShort {
  // "Series B $40M", "Public (FBKM)", "Acquired".
  main: string;
  // "2mo", written as text on the right; null when there is no date.
  age: string | null;
  // Under 90 days: the stage in bold ink and the age in the accent colour.
  is_recent: boolean;
}

function monthsAgo(month: string | null | undefined, today: Date | null): string | null {
  if (!month || !today) return null;
  const days = daysSince(month.length === 7 ? month + '-01' : month, today);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days < 30) return '<1mo';
  if (days < 365) return Math.round(days / 30.44) + 'mo';
  return Math.round(days / 365) + 'y';
}

export function fundingShort(row: RankedAccount): FundingShort | null {
  const reading = readFunding(row);
  const today = bookToday(row);
  if (reading.kind === 'unknown') return null;
  if (reading.kind === 'public') return { main: `Public (${reading.detail})`, age: null, is_recent: false };
  if (reading.kind === 'acquired') {
    return { main: 'Acquired', age: monthsAgo(row.funding?.acquired?.date ?? null, today), is_recent: false };
  }
  const round = row.funding?.latest_round ?? null;
  const roundDay = round?.date ?? null;
  const is_recent = Boolean(roundDay && today
    && daysSince(roundDay.length === 7 ? roundDay + '-01' : roundDay, today) <= 90);
  return {
    main: [reading.label, formatUsd(round?.amount_usd ?? null)].filter(Boolean).join(' '),
    age: monthsAgo(roundDay, today),
    is_recent,
  };
}

export interface VentureTie {
  fund: string;
  short_name: string;
  // Named in the company's latest round.
  in_latest: boolean;
  rounds: string;
}

export interface VentureSummary {
  // 'none': Apollo read the investor list and none are ours. 'unknown': we couldn't find out.
  kind: 'funds' | 'none' | 'unknown';
  ties: VentureTie[];
  hint: string;
}

export function ventureSummary(row: RankedAccount): VentureSummary {
  const relationship = readRelationship(row);
  if (relationship.kind !== 'funds') {
    return { kind: relationship.kind, ties: [], hint: relationship.hint };
  }
  const latestDay = row.funding?.latest_round?.date ?? null;
  const ties = relationship.ties.map(tie => ({
    fund: tie.fund,
    short_name: fundShortName(tie.fund),
    in_latest: Boolean(latestDay && tie.rounds.some(round => round.date === latestDay)),
    rounds: tie.rounds.map(round => [round.type, monthShort(round.date)].filter(Boolean).join(' ')).join(', '),
  }));

  // The fund in the latest round first, then the payload's own strongest-first order.
  ties.sort((left, right) => Number(right.in_latest) - Number(left.in_latest));
  return { kind: 'funds', ties, hint: relationship.hint };
}

// --- Flags ------------------------------------------------------------------------------------

export function openOpportunityCount(rows: BookAccount[]): number {
  return rows.filter(row => row.opportunities.some(deal => !deal.closed)).length;
}

// Sort Why now with the pass's strong lines first, then by best_signal's own score, then the
// ranking reason's kind.
export function bestSignalScore(row: RankedAccount): number | null {
  const strongBonus = whyNowCell(row)?.pass && passStrength(row) === 'strong' ? STRONG_PASS_SORT_BONUS : 0;
  const signal = bestSignal(row);
  if (signal) return strongBonus + 100 + signal.score;
  if (row.tag) return strongBonus + WHY_NOW_RANK[row.tag.kind];
  return strongBonus || null;
}

const STRONG_PASS_SORT_BONUS = 1000;
